import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import makeStyles from '@material-ui/core/styles/makeStyles';
import TableCell from '@material-ui/core/TableCell';
import classnames from 'classnames';
import React, {useEffect, useState} from 'react';
import {useScheduleContext} from '../Utils/ScheduleContextProvider';

const useStyles = makeStyles(theme => ({
    header: {
        '& *': {
            fontWeight: 'bold',
        },
    },
    currentWeek: {
        backgroundColor: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
    },
    input: {
        color: 'inherit',
        border: 'none',
        backgroundColor: 'transparent',
        margin: 0,
        padding: 0,
        width: 30,
        textAlign: 'right',
    },
    actual: {
        display: 'inline-block',
        marginLeft: theme.spacing(1),
        color: theme.palette.secondary[theme.palette.type === 'dark' ? 'light' : 'dark'],
    },
    actualHeader: {
        color: theme.palette.secondary[theme.palette.type === 'dark' ? 'light' : 'dark'],
    },
}));

type Schedule = {
    scheduled : number;
    actual ?: number;
};

type Props = {
    employeeId ?: number;
    projectId ?: number;
    startOfWeek ?: string;
    currentWeek ?: string;
    schedule : Schedule;
    header ?: boolean;
    disabled ?: boolean;
    onFillRight ?: (hours : number, startOfWeek : string) => Promise<void>;
    onApplyToAllVisible ?: (hours : number, startOfWeek : string) => Promise<void>;
};

type MenuPosition = {
    x : number;
    y : number;
};

const ScheduleCell : React.FC<Props> = ({
    employeeId,
    projectId,
    startOfWeek,
    currentWeek,
    schedule,
    header,
    disabled,
    onFillRight,
    onApplyToAllVisible,
} : Props) => {
    const classes = useStyles();
    const [inputValue, setInputValue] = useState<string>(schedule.scheduled.toString());
    const [updating, setUpdating] = useState(false);
    const scheduleContext = useScheduleContext();
    const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);

    useEffect(() => setInputValue(schedule.scheduled.toString()), [schedule.scheduled]);

    const handleChange = (event : React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(event.currentTarget.value);
    };

    const handleBlur = async (event : React.FocusEvent<HTMLInputElement>) => {
        setUpdating(true);
        let value = parseInt(event.currentTarget.value, 10);

        if (Number.isNaN(value)) {
            value = 0;
        } else if (value < 0) {
            value = 0;
        }

        setInputValue(value.toString());

        if (employeeId && projectId && startOfWeek && value !== schedule.scheduled) {
            await scheduleContext.updateSchedule(employeeId, projectId, startOfWeek, value);
        }

        setUpdating(false);
    };

    const handleContextMenu = (event : React.MouseEvent<HTMLInputElement>) => {
        event.preventDefault();
        event.currentTarget.blur();
        setMenuPosition({x: event.clientX - 2, y: event.clientY - 4});
    };

    let className = undefined;

    if (header) {
        className = classes.header;
    } else if (currentWeek && currentWeek === startOfWeek) {
        className = classes.currentWeek;
    }

    const menuItems = [];

    if (onFillRight && startOfWeek) {
        menuItems.push((
            <MenuItem key="fill-right" onClick={() => {
                setMenuPosition(null);
                onFillRight(parseInt(inputValue, 10), startOfWeek);
            }}>Fill right</MenuItem>
        ));
    }

    if (onApplyToAllVisible && startOfWeek) {
        menuItems.push((
            <MenuItem key="apply-to-all" onClick={() => {
                setMenuPosition(null);
                onApplyToAllVisible(parseInt(inputValue, 10), startOfWeek);
            }}>Apply to all visible</MenuItem>
        ));
    }

    return (
        <TableCell className={className}>
            {!header ? (
                <input
                    type="text"
                    value={inputValue}
                    maxLength={2}
                    className={classes.input}
                    onChange={handleChange}
                    onFocus={event => event.currentTarget.select()}
                    onBlur={handleBlur}
                    disabled={updating || disabled}
                    onContextMenu={menuItems.length === 0 ? undefined : handleContextMenu}
                />
            ) : (
                <input
                    type="text"
                    value={inputValue}
                    disabled={true}
                    className={classes.input}
                />
            )}

            {schedule.actual && (
                <span className={classnames(classes.actual, header && classes.actualHeader)}>
                    {schedule.actual.toFixed(2)}
                </span>
            )}

            {menuItems.length > 0 && (
                <Menu
                    open={Boolean(menuPosition)}
                    onClose={() => setMenuPosition(null)}
                    anchorReference="anchorPosition"
                    anchorPosition={menuPosition ? {top: menuPosition.y, left: menuPosition.x} : undefined}
                >
                    {menuItems}
                </Menu>
            )}
        </TableCell>
    );
};

export default ScheduleCell;
